import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Printer, Search, Send } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import { resolveBlankPHF, sendBlankPHFStandalone } from '@/services/deals.service.js';
import { ApiError } from '@/lib/api.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// On-the-fly blank PHF generator. Type a tail, get a printable form, a
// copyable link, or an email send — no deal required. Resolve prefills
// make/model/year from the FAA registry and auto-matches the newest session
// carrying the tail; a matched deal means submissions persist to that
// session, an unmatched one still reaches the broker by email as a lead.
export default function BlankPHF() {
  const [tail, setTail] = useState('');
  const [looking, setLooking] = useState(false);
  const [resolved, setResolved] = useState(null); // { n_number, faa, deal }
  const [makeModel, setMakeModel] = useState('');
  const [year, setYear] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const errMsg = (err) =>
    err instanceof ApiError ? err.body?.error || err.code : err.message || 'request failed';

  const onTail = (v) => {
    let t = v.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (t.startsWith('N')) t = t.slice(1);
    setTail(t.slice(0, 5));
  };

  const lookup = async () => {
    if (!tail.trim()) return;
    setLooking(true);
    setResolved(null);
    setLink(null);
    try {
      const res = await resolveBlankPHF(`N${tail.trim()}`);
      setResolved(res);
      setMakeModel(res.faa?.make_model || '');
      setYear(res.faa?.year || '');
    } catch (err) {
      toast.error(`Lookup failed: ${errMsg(err)}`);
    } finally {
      setLooking(false);
    }
  };

  const printForm = () => {
    const qs = new URLSearchParams({ makeModel, year });
    if (resolved?.deal?.crm_deal_id) qs.set('dealId', resolved.deal.crm_deal_id);
    window.open(
      `https://ndemnifly.com/phf/print/${encodeURIComponent(resolved.n_number)}?${qs}`,
      '_blank',
      'noopener'
    );
  };

  // toEmail present → mint + send; absent → mint only and surface the link.
  const mint = async (toEmail) => {
    setBusy(true);
    try {
      const res = await sendBlankPHFStandalone({
        n_number: resolved.n_number,
        make_model: makeModel,
        year,
        deal_id: resolved?.deal?.crm_deal_id || undefined,
        email: toEmail || undefined,
      });
      if (toEmail) {
        toast.success(`Blank PHF for ${resolved.n_number} sent to ${toEmail}`);
        setEmail('');
        setLink(res.link);
      } else {
        setLink(res.link);
        try {
          await navigator.clipboard.writeText(res.link);
          toast.success('PHF link copied. Paste it into a text or email.');
        } catch {
          toast.info('Link ready below. Copy it manually.');
        }
      }
    } catch (err) {
      toast.error(`Blank PHF failed: ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-sm text-text outline-none focus:border-border-strong';
  const btnCls =
    'inline-flex items-center gap-1.5 rounded-[5px] border border-border bg-surface px-3 py-1.5 text-[12.5px] text-text hover:border-border-strong hover:bg-surface-strong disabled:opacity-50';

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-6 md:px-6">
      <h1 className="text-[19px] font-semibold tracking-tight text-cream">Blank PHF</h1>
      <p className="mt-1 text-xs text-text-muted">
        Generate a Pilot History Form for any aircraft on the fly. Print it, copy the link,
        or email it. The form isn't tied to a person, so it can be handed or forwarded to
        a pilot whose contact info you don't have.
      </p>

      <div className="mt-5 flex items-center gap-2">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-lg font-bold text-text-dim">
            N
          </span>
          <input
            type="text"
            value={tail}
            onChange={(e) => onTail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !looking) lookup(); }}
            placeholder="6867Z"
            autoFocus
            className="w-full rounded-[5px] border border-border bg-surface py-2.5 pl-8 pr-3 font-mono text-lg uppercase tracking-widest text-text outline-none focus:border-border-strong"
          />
        </div>
        <button type="button" onClick={lookup} disabled={looking || !tail.trim()} className={`${btnCls} py-2.5`}>
          <Search size={14} strokeWidth={1.5} />
          {looking ? 'Looking…' : 'Look up'}
        </button>
      </div>

      {resolved ? (
        <div className="mt-4 space-y-4 rounded-card border border-border bg-surface p-4">
          <div>
            <div className="font-mono text-sm font-semibold text-text">{resolved.n_number}</div>
            <div className="mt-0.5 text-xs text-text-muted">
              {resolved.faa
                ? 'FAA registry match. Edit the fields if needed.'
                : 'Not in the FAA registry. Fill in what you know, or leave blank.'}
            </div>
          </div>

          <div className="grid grid-cols-[1fr_90px] gap-3">
            <div>
              <label className="mb-1 block text-xs text-text-muted">Make &amp; model</label>
              <input type="text" value={makeModel} onChange={(e) => setMakeModel(e.target.value)} placeholder="Cessna 421C" className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-muted">Year</label>
              <input type="text" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="1981" className={inputCls} />
            </div>
          </div>

          {resolved.deal?.crm_deal_id ? (
            <p className="text-xs text-text-muted">
              Linked to{' '}
              {resolved.deal.crm_account_id ? (
                <Link
                  to={`/account/${resolved.deal.crm_account_id}/deal/${resolved.deal.crm_deal_id}`}
                  className="text-text underline-offset-2 hover:underline"
                >
                  {resolved.deal.account_name || 'a deal on file'}
                </Link>
              ) : (
                <span className="text-text">{resolved.deal.account_name || 'a deal on file'}</span>
              )}
              . Submissions attach to that deal's session.
            </p>
          ) : (
            <p className="text-xs text-text-muted">
              No deal on file for this tail. Submissions arrive in your inbox as a new lead.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3.5">
            <button type="button" onClick={printForm} disabled={busy} className={btnCls}>
              <Printer size={13} strokeWidth={1.5} />
              Print form
            </button>
            <button type="button" onClick={() => mint(null)} disabled={busy} className={btnCls}>
              <Copy size={13} strokeWidth={1.5} />
              Copy link
            </button>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-text-muted">Or email it</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && EMAIL_RE.test(email.trim()) && !busy) mint(email.trim()); }}
                placeholder="owner@example.com"
                className={inputCls}
              />
            </div>
            <button
              type="button"
              disabled={busy || !EMAIL_RE.test(email.trim())}
              onClick={() => mint(email.trim())}
              className="inline-flex items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50"
            >
              <Send size={13} strokeWidth={1.5} />
              {busy ? 'Working…' : 'Send'}
            </button>
          </div>

          {link ? (
            <div>
              <label className="mb-1 block text-xs text-text-muted">Form link</label>
              <input
                type="text"
                readOnly
                value={link}
                onClick={(e) => e.target.select()}
                className="w-full rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-xs text-text outline-none"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
